/**
 * useLocationPicker Hook
 * Manages location picking, searching, and coordinate handling
 */

import { useState, useRef, useCallback } from 'react';
import { reverseGeocode } from '../domain/useCases/Location/reverseGeocode.js';
import { searchLocation } from '../domain/useCases/Location/searchLocation.js';
import { extractNepalLocationFromAddress } from '../utils/nepalLocationMapping';

// Helper function to find location codes from names
const findLocationCodes = async (provinceNameFromAPI, districtNameFromAPI, municipalityNameFromAPI) => {
  try {
    const response = await fetch('/json/location.json');
    if (!response.ok) return { province: '', district: '', municipality: '' };
    
    const data = await response.json();
    const locationData = data.item;
    
    let provinceCode = '';
    let districtCode = '';
    let municipalityCode = '';
    
    // Find province by name (case-insensitive partial match)
    if (provinceNameFromAPI) {
      const province = locationData.provinces?.find(p => 
        p.label.toLowerCase().includes(provinceNameFromAPI.toLowerCase()) ||
        p.alt_name.toLowerCase().includes(provinceNameFromAPI.toLowerCase()) ||
        provinceNameFromAPI.toLowerCase().includes(p.label.toLowerCase())
      );
      if (province) {
        provinceCode = province.code;
        
        // Find district within this province
        if (districtNameFromAPI) {
          const district = province.districts?.find(d =>
            d.label.toLowerCase().includes(districtNameFromAPI.toLowerCase()) ||
            d.alt_name.toLowerCase().includes(districtNameFromAPI.toLowerCase()) ||
            districtNameFromAPI.toLowerCase().includes(d.label.toLowerCase())
          );
          if (district) {
            districtCode = district.code;
            
            // Find municipality within this district
            if (municipalityNameFromAPI) {
              const municipality = district.municipalities?.find(m =>
                m.label.toLowerCase().includes(municipalityNameFromAPI.toLowerCase()) ||
                m.alt_name.toLowerCase().includes(municipalityNameFromAPI.toLowerCase()) ||
                municipalityNameFromAPI.toLowerCase().includes(m.label.toLowerCase())
              );
              if (municipality) {
                municipalityCode = municipality.code;
              }
            }
          }
        }
      }
    }
    
    return { province: provinceCode, district: districtCode, municipality: municipalityCode };
  } catch (err) {
    console.error('Error finding location codes:', err);
    return { province: '', district: '', municipality: '' };
  }
};

export const useLocationPicker = (onLocationChange) => {
  const [mapCenter, setMapCenter] = useState([28.3949, 84.1240]);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);

  /**
   * Get location details from coordinates
   */
  const getLocationFromCoordinates = useCallback(async (lat, lon, preserveAddress = false) => {
    try {
      const result = await reverseGeocode(lat, lon);
      if (result) {
        const address = result.display_name || '';
        
        // Extract location names from Nominatim response
        const locationInfo = extractNepalLocationFromAddress(result.address || {});
        
        // Convert location names to codes
        const locationCodes = await findLocationCodes(
          locationInfo.province,
          locationInfo.district,
          locationInfo.municipality
        );

        const locationData = {
          latitude: lat.toFixed(8),
          longitude: lon.toFixed(8),
          mapAddress: {
            generalAddress: preserveAddress ? undefined : address, // Don't overwrite if preserving
            street: result.address?.road || result.address?.neighbourhood || '',
            province: locationCodes.province,
            district: locationCodes.district,
            municipality: locationCodes.municipality,
          },
        };

        console.log('Location data from reverse geocode:', locationData);
        console.log('Setting generalAddress:', address);
        console.log('Preserve address flag:', preserveAddress);

        setMapCenter([lat, lon]);
        if (onLocationChange) {
          onLocationChange(locationData);
        }
        return locationData;
      }
    } catch (err) {
      console.error('Reverse geocode error:', err);
      throw err;
    }
  }, [onLocationChange]);

  /**
   * Search for location by query
   */
  const handleSearchLocation = useCallback((query) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.trim().length > 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await searchLocation(query);
          if (results.length > 0) {
            const result = results[0];
            await getLocationFromCoordinates(parseFloat(result.lat), parseFloat(result.lon));
          }
        } catch (err) {
          console.error('Search error:', err);
        }
      }, 800);
    }
  }, [getLocationFromCoordinates]);

  /**
   * Cleanup on unmount
   */
  const cleanup = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  return {
    mapCenter,
    setMapCenter,
    searchQuery,
    setSearchQuery,
    getLocationFromCoordinates,
    handleSearchLocation,
    cleanup,
  };
};
